import type { DREGerencialView } from "../types/dre-gerencial.types";
import {
  formatarMoedaBRL,
  formatarPercentual,
} from "./financeiro-formatters";
import { rotuloCategoriaDespesa } from "./map-dre-api-to-view";

export type DreLinhaVariant = "main" | "sub" | "highlight" | "total" | "margem";

export type DreLinhaNivel = "principal" | "sub";

export type DreGerencialLinha = {
  conta: string;
  valorFormatado: string;
  valorNumerico?: number;
  isPercentual?: boolean;
  variant: DreLinhaVariant;
  nivel: DreLinhaNivel;
  valorCorErro?: boolean;
  valorCorSucesso?: boolean;
};

export function buildDreGerencialLinhas(v: DREGerencialView): DreGerencialLinha[] {
  const rows: DreGerencialLinha[] = [];
  const push = (r: Omit<DreGerencialLinha, "nivel"> & { nivel?: DreLinhaNivel }) => {
    rows.push({
      ...r,
      nivel: r.nivel ?? (r.variant === "sub" ? "sub" : "principal"),
    });
  };

  push({
    conta: "Receita bruta",
    valorFormatado: formatarMoedaBRL(v.receitaBrutaTotal),
    valorNumerico: v.receitaBrutaTotal,
    variant: "main",
  });
  push({
    conta: "Vendas de produtos / serviços",
    valorFormatado: formatarMoedaBRL(v.receitaProdutos),
    valorNumerico: v.receitaProdutos,
    variant: "sub",
  });
  push({
    conta: "Inscrições em eventos",
    valorFormatado: formatarMoedaBRL(v.receitaInscricoes),
    valorNumerico: v.receitaInscricoes,
    variant: "sub",
  });
  push({
    conta: "Fretes",
    valorFormatado: formatarMoedaBRL(v.receitaFretes),
    valorNumerico: v.receitaFretes,
    variant: "sub",
  });
  if (v.receitaPatrocinios) {
    push({
      conta: "Patrocínios",
      valorFormatado: formatarMoedaBRL(v.receitaPatrocinios),
      valorNumerico: v.receitaPatrocinios,
      variant: "sub",
    });
  }
  if (v.receitaAjustes) {
    push({
      conta: "Ajustes manuais de receita",
      valorFormatado: formatarMoedaBRL(v.receitaAjustes),
      valorNumerico: v.receitaAjustes,
      variant: "sub",
    });
  }

  const jurosLoja = v.receitaDetalhada?.loja.jurosParcelamento ?? 0;
  const jurosInsc = v.receitaDetalhada?.inscricoes.jurosParcelamento ?? 0;
  if (jurosLoja >= 0.01) {
    push({
      conta: "Juros de parcelamento — loja (informativo)",
      valorFormatado: formatarMoedaBRL(jurosLoja),
      valorNumerico: jurosLoja,
      variant: "sub",
    });
  }
  if (jurosInsc >= 0.01) {
    push({
      conta: "Juros de parcelamento — inscrições (informativo)",
      valorFormatado: formatarMoedaBRL(jurosInsc),
      valorNumerico: jurosInsc,
      variant: "sub",
    });
  }

  push({
    conta: "(-) Deduções",
    valorFormatado: formatarMoedaBRL(v.deducoesTotal),
    valorNumerico: v.deducoesTotal,
    variant: "main",
  });
  push({
    conta: "Taxas de gateway",
    valorFormatado: formatarMoedaBRL(v.taxasGateway),
    valorNumerico: v.taxasGateway,
    variant: "sub",
  });
  push({
    conta: "Estornos e cancelamentos financeiros",
    valorFormatado: formatarMoedaBRL(v.estornos),
    valorNumerico: v.estornos,
    variant: "sub",
  });

  push({
    conta: "Receita líquida",
    valorFormatado: formatarMoedaBRL(v.receitaLiquida),
    valorNumerico: v.receitaLiquida,
    variant: "highlight",
    valorCorErro: v.receitaLiquida < 0,
  });

  push({
    conta: "(-) CMV",
    valorFormatado: formatarMoedaBRL(v.cmvTotal),
    valorNumerico: v.cmvTotal,
    variant: "main",
  });
  push({
    conta: "Custo dos produtos (pedidos)",
    valorFormatado: formatarMoedaBRL(v.cmvTotal),
    valorNumerico: v.cmvTotal,
    variant: "sub",
  });

  push({
    conta: "Lucro bruto",
    valorFormatado: formatarMoedaBRL(v.lucroBruto),
    valorNumerico: v.lucroBruto,
    variant: "highlight",
    valorCorErro: v.lucroBruto < 0,
  });

  push({
    conta: "Margem bruta",
    valorFormatado: formatarPercentual(v.margemBruta),
    valorNumerico: v.margemBruta,
    isPercentual: true,
    variant: "margem",
    valorCorErro: v.margemBruta < 0,
  });

  push({
    conta: "(-) Despesas operacionais",
    valorFormatado: formatarMoedaBRL(v.despesasTotal),
    valorNumerico: v.despesasTotal,
    variant: "main",
  });

  const categoriasOrdenadas = Object.entries(v.despesasPorCategoria)
    .filter(([, val]) => val !== 0)
    .sort(([a], [b]) =>
      rotuloCategoriaDespesa(a).localeCompare(rotuloCategoriaDespesa(b)),
    );
  for (const [key, val] of categoriasOrdenadas) {
    push({
      conta: rotuloCategoriaDespesa(key),
      valorFormatado: formatarMoedaBRL(val),
      valorNumerico: val,
      variant: "sub",
    });
  }

  push({
    conta: "Lucro operacional",
    valorFormatado: formatarMoedaBRL(v.lucroOperacional),
    valorNumerico: v.lucroOperacional,
    variant: "total",
    valorCorErro: v.lucroOperacional < 0,
    valorCorSucesso: v.lucroOperacional >= 0,
  });

  push({
    conta: "Margem operacional",
    valorFormatado: formatarPercentual(v.margemOperacional),
    valorNumerico: v.margemOperacional,
    isPercentual: true,
    variant: "margem",
    valorCorErro: v.margemOperacional < 0,
  });

  return rows;
}
